/**
 * IndexedDB cache for desk-open@v1 — plan/33.
 * Separate from POS `icraftx-pos`.
 */

import type { DeskOpenPoRow, DeskOpenSoRow } from "@icraftx/shared/v1";

const DB_NAME = "icraftx-desk";
const DB_VERSION = 1;

export type DeskOpenWatermark = {
  lastUpdated: string | null;
  lastId: string | null;
};

export type DeskOpenCache = {
  companyRef: string;
  salesOrders: DeskOpenSoRow[];
  purchaseOrders: DeskOpenPoRow[];
  soWatermark: DeskOpenWatermark;
  poWatermark: DeskOpenWatermark;
  savedAt: string;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("IDB open failed"));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("deskOpen")) {
        db.createObjectStore("deskOpen", { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IDB tx failed"));
    tx.onabort = () => reject(tx.error ?? new Error("IDB tx aborted"));
  });
}

export async function saveDeskOpenCache(cache: DeskOpenCache): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("deskOpen", "readwrite");
  tx.objectStore("deskOpen").put({ key: cache.companyRef, ...cache });
  await txDone(tx);
  db.close();
}

export async function loadDeskOpenCache(
  companyRef: string,
): Promise<DeskOpenCache | null> {
  const db = await openDb();
  const tx = db.transaction("deskOpen", "readonly");
  const req = tx.objectStore("deskOpen").get(companyRef);
  const row = await new Promise<DeskOpenCache | undefined>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result as DeskOpenCache | undefined);
    req.onerror = () => reject(req.error ?? new Error("IDB get failed"));
  });
  await txDone(tx);
  db.close();
  if (!row) return null;
  return {
    companyRef: row.companyRef,
    salesOrders: row.salesOrders ?? [],
    purchaseOrders: row.purchaseOrders ?? [],
    soWatermark: row.soWatermark ?? { lastUpdated: null, lastId: null },
    poWatermark: row.poWatermark ?? { lastUpdated: null, lastId: null },
    savedAt: row.savedAt,
  };
}
